import Avatar from '@dd3tech/wiggui/Avatar'
import Flex from '@dd3tech/wiggui/Flex'
import Spinner from '@dd3tech/wiggui/Spinnner'
import Text from '@dd3tech/wiggui/Text'
import { Transition } from '@dd3tech/wiggui/Transition'
import { lazy, useState } from 'react'
import {
  MdOutlineAccountCircle,
  MdOutlineLogin,
  MdOutlineSwapHoriz
} from 'react-icons/md'
import { useRoles } from '../../../../auth/hooks/useRoles'
import { useSession } from '../../../../auth/hooks/useSession'
import { createUrlMedia } from '../../../../utils/src/media-image'
import { composeClasses } from '../../../../utils/src/strings'
import { getTypeUserLabel } from '../../../../utils/src/type-user-label'
import HardLink from '../../../components/hard-link/HardLink'
import { ROUTES } from '../../../constants/routes'
import { useAuth } from '../../../hooks/useAuth'
import { useLayoutV2Store } from '../../../store/layout-v2.store'

const DropdownComponent = lazy(() => import('@dd3tech/wiggui/Dropdown'))

const Dropdown = Object.assign(DropdownComponent, {
  Trigger: lazy(() =>
    import('@dd3tech/wiggui/Dropdown').then((mod) => ({
      default: mod.Dropdown.Trigger
    }))
  ),
  Menu: lazy(() =>
    import('@dd3tech/wiggui/Dropdown').then((mod) => ({
      default: mod.Dropdown.Menu
    }))
  )
})

interface ButtonProfileProps {
  isExpandedMenu: boolean
}

export const ButtonProfile = ({ isExpandedMenu }: ButtonProfileProps) => {
  const { setIsExpandedMenu, isEnableFixedMenu } = useLayoutV2Store()
  const { profile, authUser } = useSession()
  const { isLoadingLogout, handleLogout } = useAuth()
  const {
    rolesExist,
    isAdminAgency,
    isBasicUser,
    isBrokerWithAgency,
    isIndependentAgent,
    roles
  } = useRoles()

  const [showOptions, setShowOptions] = useState(false)

  const getAccountUrl = () => {
    if (roles?.DEVELOPER_USER) {
      return ROUTES.DEVELOPMENT_ACCOUNT
    }
    if (roles?.ROLE_ADMIN_DEVELOPER && authUser?.isSelectDevelop) {
      return ROUTES.DEVELOPMENT_ACCOUNT
    }
    if (rolesExist && !roles?.ASSOCIATION) {
      return ROUTES.ACCOUNT_PROFILE
    }

    return ROUTES.ASSOCIATION_AGENCIES
  }

  const onClickProfile = () => {
    if (!isEnableFixedMenu) {
      setIsExpandedMenu(false)
    }
    setShowOptions(false)
  }

  const profileOptions = [
    {
      text: 'Cambiar rol',
      onClick: () => (window.location.href = ROUTES.LOGIN_ROL),
      isShow:
        !roles?.DEVELOPER_USER &&
        !roles?.ASSOCIATION &&
        Boolean(roles?.DEVELOPER || roles?.ROLE_ADMIN_DEVELOPER)
    },
    {
      text: isLoadingLogout ? 'Cerrando sesión...' : 'Cerrar sesión',
      onClick: handleLogout,
      isShow: rolesExist,
      className: 'rounded-b-xl'
    }
  ]

  const avatarUrl = profile?.photo
    ? createUrlMedia({ id: profile?.photo, size: 's' })
    : ''

  const { agencyNameLabel, typeUserLabel } = getTypeUserLabel({
    agencyName: profile?.agencyName || '',
    isAdminAgency,
    isBasicUser,
    isBrokerWithAgency,
    isIndependentAgent
  })

  return (
    <Dropdown
      isOpen={showOptions}
      setIsOpen={setShowOptions}
      onClose={() => setShowOptions(false)}
      defaultPlacement="right-start"
      strategyPosition="fixed"
      className="w-full z-3"
    >
      <Dropdown.Trigger className="w-full">
        <button
          type="button"
          className={composeClasses(
            'flex justify-between w-full items-center gap-2.5 h-10 min-h-[40px] px-[10px] rounded-xl bg-surface-gray-10 hover:bg-[#DEE5F1] active:bg-[#b6bbc5] transition-colors duration-300 ease-in-out'
          )}
        >
          <Flex dir="row" items="center" justify="start" gap="gap-2.5" wFull>
            <Avatar
              containerClassName="min-w-[18px] min-h-[18px] max-h-[18px] max-w-[18px]"
              classFullName="text-body-bold-s"
              avatarUrl={avatarUrl}
              fullName={profile?.fullname}
            />

            <Transition duration={300} show={isExpandedMenu} className="w-full">
              <Flex dir="col" items="start" justify="center" wFull>
                <Text className="text-secondary text-body-xs flex flex-row items-center justify-center">
                  <span>{typeUserLabel}</span>
                  {agencyNameLabel && (
                    <>
                      <div className="mx-2 w-1 h-1 rounded-full bg-secondary inline-block" />
                      <span className="whitespace-nowrap max-w-[50px] text-ellipsis overflow-hidden">
                        {agencyNameLabel}
                      </span>
                    </>
                  )}
                </Text>
                <p
                  aria-label={profile?.fullname ?? 'Perfil'}
                  className="text-primary text-left text-body-bold-m whitespace-nowrap text-ellipsis overflow-hidden w-full max-w-[120px]"
                >
                  {profile?.fullname ?? 'Perfil'}
                </p>
              </Flex>
            </Transition>
          </Flex>
        </button>
      </Dropdown.Trigger>
      <Dropdown.Menu className="!py-0 max-w-[200px]">
        {Boolean(
          (rolesExist && !roles?.ASSOCIATION) ||
            roles?.DEVELOPER_USER ||
            roles?.ROLE_ADMIN_DEVELOPER
        ) && (
          <HardLink
            href={getAccountUrl()}
            className={composeClasses(
              'flex justify-start w-full rounded-t-xl items-center gap-3 p-[10px] hover:bg-[#DEE5F1] active:bg-[#b6bbc5] transition-colors duration-300 ease-in-out'
            )}
            onClick={onClickProfile}
          >
            <MdOutlineAccountCircle className="min-w-[16px] min-h-[16px] text-primary" />
            <p
              className={composeClasses(
                'text-body-s group-active:text-body-bold-s text-primary whitespace-nowrap text-ellipsis overflow-hidden w-full text-left max-w-[110px]'
              )}
            >
              Cuenta
            </p>
          </HardLink>
        )}
        {profileOptions?.map(({ className, text, onClick, isShow }) => {
          if (!isShow) {
            return null
          }

          const isRole = text === 'Cambiar rol'

          return (
            <button
              type="button"
              className={composeClasses(
                className,
                'flex justify-start w-full items-center gap-3 p-[10px] hover:bg-[#DEE5F1] active:bg-[#b6bbc5] transition-colors duration-300 ease-in-out'
              )}
              key={text}
              onClick={() => onClick(undefined)}
            >
              {!isRole && (
                <>
                  {isLoadingLogout ? (
                    <Spinner customBorderColor="border-t-destructive" />
                  ) : (
                    <MdOutlineLogin className="min-w-[16px] min-h-[16px] text-destructive" />
                  )}
                </>
              )}
              {isRole && (
                <MdOutlineSwapHoriz className="min-w-[16px] min-h-[16px] text-primary" />
              )}
              <p
                className={composeClasses(
                  isRole ? 'text-primary' : 'text-destructive',
                  'text-body-s group-active:text-body-bold-s whitespace-nowrap text-ellipsis overflow-hidden w-full text-left max-w-[110px]'
                )}
              >
                {text}
              </p>
            </button>
          )
        })}
      </Dropdown.Menu>
    </Dropdown>
  )
}
